import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const START_YEAR = 2008;
const END_YEAR = 2026;
const TOTAL_YEARS = END_YEAR - START_YEAR; // 18

export default function Claude({ onClose }) {
  const [year, setYear] = useState(START_YEAR);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) return;
    const interval = setInterval(() => {
      setYear(y => {
        if (y >= END_YEAR - 1) {
          clearInterval(interval);
          setDone(true);
          return END_YEAR;
        }
        return y + 1;
      });
    }, 220);
    return () => clearInterval(interval);
  }, [done]);

  const progress = Math.round(((year - START_YEAR) / TOTAL_YEARS) * 100);

  return (
    <Div>
      <div className="claude-body">
        {!done ? (
          <>
            <div className="claude-logo">
              <img src={`${process.env.PUBLIC_URL}/claude-icon.png`} alt="Claude" className="claude-icon-img" />
            </div>
            <div className="claude-title">Claude AI</div>
            <div className="claude-subtitle">by Anthropic</div>
            <div className="claude-status">
              Please wait<DotAnim />
            </div>
            <div className="claude-year-label">
              Current year: <span className="claude-year">{year}</span>
            </div>
            <div className="claude-bar-wrap">
              <div className="claude-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="claude-eta">
              Claude will be available in <strong>{END_YEAR}</strong>.
              <br />
              <span className="claude-note">
                {year < 2015
                  ? `${END_YEAR - year} years to go. Maybe go outside?`
                  : year < 2020
                  ? `Getting closer... GPT-2 just dropped, don't get excited.`
                  : year < 2023
                  ? `ChatGPT launched. Impressive, but not Claude.`
                  : `Almost there... Anthropic is putting the finishing touches on...`}
              </span>
            </div>
          </>
        ) : (
          <div className="claude-ready">
            <div className="claude-logo">
              <img src={`${process.env.PUBLIC_URL}/claude-icon.png`} alt="Claude" className="claude-icon-img claude-pulse" />
            </div>
            <div className="claude-title">Claude AI is ready.</div>
            <div className="claude-subtitle">Welcome to 2026.</div>
            <div className="claude-ready-text">
              That took 18 years.<br />
              Worth the wait, honestly.<br /><br />
              <span className="claude-note">
                "Half the things Claude can do in 2026 were science fiction in 2008.<br />
                The other half are still kind of science fiction.<br />
                We just call it 'AI' now."
              </span>
            </div>
            <button className="claude-close-btn" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </Div>
  );
}

const dotAnim = keyframes`
  0%   { content: '.'; }
  33%  { content: '..'; }
  66%  { content: '...'; }
  100% { content: '.'; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%       { transform: scale(1.08); opacity: 0.85; }
`;

const DotAnim = styled.span`
  &::after {
    content: '...';
    animation: ${dotAnim} 1.2s steps(1) infinite;
  }
`;

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Tahoma, sans-serif;

  .claude-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 30px 40px;
    text-align: center;
    color: #fff;
    max-width: 380px;
  }

  .claude-logo {
    margin-bottom: 4px;
  }
  .claude-icon-img {
    width: 64px;
    height: 64px;
    object-fit: contain;
  }
  .claude-pulse {
    animation: ${pulse} 1.5s ease-in-out infinite;
  }

  .claude-title {
    font-size: 22px;
    font-weight: bold;
    color: #e8c07a;
    letter-spacing: 1px;
  }
  .claude-subtitle {
    font-size: 11px;
    color: rgba(255,255,255,0.5);
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: -4px;
  }

  .claude-status {
    font-size: 13px;
    color: rgba(255,255,255,0.8);
    margin-top: 6px;
  }

  .claude-year-label {
    font-size: 12px;
    color: rgba(255,255,255,0.6);
  }
  .claude-year {
    font-size: 18px;
    font-weight: bold;
    color: #e8c07a;
  }

  .claude-bar-wrap {
    width: 280px;
    height: 10px;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 5px;
    overflow: hidden;
  }
  .claude-bar-fill {
    height: 100%;
    background: linear-gradient(to right, #e8a040, #e8c07a);
    border-radius: 5px;
    transition: width 0.2s linear;
  }

  .claude-eta {
    font-size: 11px;
    color: rgba(255,255,255,0.65);
    line-height: 1.6;
    margin-top: 4px;
  }
  .claude-note {
    font-style: italic;
    color: rgba(255,255,255,0.45);
    font-size: 10px;
  }

  .claude-ready {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .claude-ready-text {
    font-size: 12px;
    color: rgba(255,255,255,0.75);
    line-height: 1.7;
  }
  .claude-close-btn {
    margin-top: 10px;
    background: linear-gradient(to bottom, #e8c07a, #c8a050);
    color: #1a1a2e;
    border: none;
    padding: 6px 24px;
    font-size: 12px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    border-radius: 3px;
    cursor: pointer;
    &:hover { background: linear-gradient(to bottom, #f0d090, #d8b060); }
    &:active { transform: translateY(1px); }
  }
`;
